/**
 * UF-1 (UFC-FOLLOWUP-BULK-RESOLVE) — fold a list of
 * `FieldConflictGroup`s through `applyConflictResolution` against a
 * single base `EvidenceConflict[]` so the caller can persist ONCE
 * (atomic-from-the-user's-perspective, R1/HARD-RULE-8).
 *
 * Why a separate module: UFC-2's `handleResolveGroup` already folds
 * the members of ONE group atomically; UF-1 needs to fold N groups
 * (each carrying M members) atomically. Same primitive
 * (`applyConflictResolution`), same single-persist contract — just one
 * level of nesting deeper. Keeping the helper pure + module-scoped:
 *   - Lets vitest pin the N-group atomicity contract without rendering
 *     React.
 *   - Keeps the panel component small (the UI just dispatches a
 *     strategy + receives the next array to persist).
 *
 * Strategies map to the three CTAs the panel exposes:
 *   - 'prefer-provider'   — Confirm all provider values.
 *   - 'prefer-doc-first'  — Use first document value for each group.
 *   - 'manual-review'     — Mark all for review.
 *
 * Notes on `'prefer-doc-first'`:
 *   - When a group has ZERO doc-side values (pure 3-axis authored /
 *     report-derived rows), the group is stamped `'prefer-provider'`
 *     instead — falling silent on the doc dimension when none exists
 *     is the truthful answer ("we don't have a doc value to pick").
 *   - When a group has ≥1 doc values, the FIRST member that carries a
 *     defined `docValue` lands `'prefer-doc'`; the rest are stamped
 *     `'prefer-provider'`. Mirrors the single-group picker semantics:
 *     "trust this doc's value, the others lose to provider".
 *
 * Pure / no React, no Firestore. Caller (panel) hands the result to
 * `persistSedConflicts` in ONE call.
 */
import type {
  EvidenceConflict,
  ConflictResolution,
} from '../services/evidenceConflict';
import type { FieldConflictGroup } from './conflictGrouping';

export type BulkStrategy =
  | 'prefer-provider'
  | 'prefer-doc-first'
  | 'manual-review';

/** Result counters surfaced to the toast + the audit metadata. */
export interface BulkResolveSummary {
  groupsResolved: number;
  membersStamped: number;
  /** Members that landed `'prefer-doc'` (only non-zero under
   *  `'prefer-doc-first'`). */
  preferDocMembers: number;
  /** Members that landed `'prefer-provider'`. */
  preferProviderMembers: number;
  /** Members that landed `'manual-review'`. */
  manualReviewMembers: number;
}

export interface BulkResolveResult {
  /** Updated conflict list — caller passes to `persistSedConflicts`. */
  next: EvidenceConflict[];
  summary: BulkResolveSummary;
}

/**
 * UF-1 — fold N groups through `applyConflictResolution` so a single
 * persist write commits every change. If the caller's persist throws,
 * NONE of the resolutions land in Firestore (the local optimistic
 * update is the caller's call to roll back).
 *
 * `applyConflictResolution` is dependency-injected so the unit test
 * can pin the call sequence without dragging the firebase boundary
 * along. In production the panel passes the real function imported
 * from `services/evidenceConflict`.
 *
 * @param baseConflicts the current SED conflict array (the panel's
 *   local-state copy, post-ICR filter is irrelevant here — the helper
 *   maps over EVERY entry).
 * @param groups the groups to resolve. Empty array short-circuits to
 *   `{ next: baseConflicts, summary: zeroed }`.
 * @param strategy one of the three CTAs.
 * @param resolvedBy the actor uid (or `'unknown'` when no user is
 *   signed in — the panel's existing fallback).
 * @param applyConflictResolution the pure stamper from
 *   `services/evidenceConflict`. Threaded for testability.
 */
export function applyBulkResolution(
  baseConflicts: readonly EvidenceConflict[],
  groups: readonly FieldConflictGroup[],
  strategy: BulkStrategy,
  resolvedBy: string,
  applyConflictResolution: (
    conflicts: EvidenceConflict[],
    match: { sectionId: string; field: string; docId: string },
    resolution: ConflictResolution,
    resolvedBy: string,
  ) => EvidenceConflict[],
): BulkResolveResult {
  let next: EvidenceConflict[] = baseConflicts as EvidenceConflict[];
  let preferDocMembers = 0;
  let preferProviderMembers = 0;
  let manualReviewMembers = 0;
  let membersStamped = 0;

  for (const g of groups) {
    // Resolve the per-group "first doc" once — picking the same member
    // index for every replay so the helper is deterministic across
    // identical inputs.
    const firstDocMember =
      strategy === 'prefer-doc-first'
        ? g.members.find(m => m.docValue !== undefined && m.docValue !== '')
        : undefined;
    // `prefer-doc-first` on a doc-less group falls back to provider —
    // we can't honour "use first doc" when there isn't one. See module
    // header for rationale.
    const effectiveStrategy: BulkStrategy =
      strategy === 'prefer-doc-first' && !firstDocMember
        ? 'prefer-provider'
        : strategy;

    for (const m of g.members) {
      let resolution: ConflictResolution;
      if (effectiveStrategy === 'manual-review') {
        resolution = 'manual-review';
        manualReviewMembers += 1;
      } else if (effectiveStrategy === 'prefer-provider') {
        resolution = 'prefer-provider';
        preferProviderMembers += 1;
      } else {
        // 'prefer-doc-first' with a real first-doc member: the picked
        // member lands prefer-doc, the others prefer-provider —
        // mirrors the single-group picker.
        if (m.docId === firstDocMember!.docId) {
          resolution = 'prefer-doc';
          preferDocMembers += 1;
        } else {
          resolution = 'prefer-provider';
          preferProviderMembers += 1;
        }
      }
      next = applyConflictResolution(
        next,
        { sectionId: g.sectionId, field: g.fieldKey, docId: m.docId },
        resolution,
        resolvedBy,
      );
      membersStamped += 1;
    }
  }

  return {
    next,
    summary: {
      groupsResolved: groups.length,
      membersStamped,
      preferDocMembers,
      preferProviderMembers,
      manualReviewMembers,
    },
  };
}

/**
 * UF-1 — short label for the toast / aria-label. Renders the same
 * vocabulary the dropdown menu uses.
 */
export const BULK_STRATEGY_LABEL: Record<BulkStrategy, string> = {
  'prefer-provider':   'Confirm all provider values',
  'prefer-doc-first':  'Use first document for each',
  'manual-review':     'Mark all for review',
};
